import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Save, Loader2, Plus, Trash2, KeyRound } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import WeeklySlotsEditor, { emptySlots } from "./WeeklySlotsEditor";

const todayISO = () => new Date().toISOString().slice(0, 10);

const PERMISSION_LABELS = [
  { key: "alumnos", label: "Alumnos" },
  { key: "citas", label: "Citas" },
  { key: "calendario", label: "Calendario" },
  { key: "reportes", label: "Reportes" },
  { key: "ciclo_dgt", label: "Ciclo DGT (CAPA/CIR)" },
];

const initialsOf = (name) =>
  (name || "").trim().split(/\s+/).map((w) => w[0] || "").join("").toUpperCase().slice(0, 3);

// V1.46 · Acceso del profesor a la plataforma (login propio + conmutadores).
function TeacherAccessSection({ teacher }) {
  const [loaded, setLoaded] = useState(false);
  const [hasUser, setHasUser] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [accEmail, setAccEmail] = useState("");
  const [accPassword, setAccPassword] = useState("");
  const [teacherCode, setTeacherCode] = useState("");
  const [permissions, setPermissions] = useState({ alumnos: false, citas: false, calendario: false, reportes: false });
  const [savingAccess, setSavingAccess] = useState(false);

  useEffect(() => {
    if (!teacher?.id) return;
    (async () => {
      try {
        const { data } = await api.get(`/teachers/${teacher.id}/access`);
        setHasUser(!!data.has_user);
        setEnabled(!!data.enabled);
        setAccEmail(data.email || teacher.email || "");
        setTeacherCode(data.teacher_code || initialsOf(teacher.name));
        setPermissions({ alumnos: false, citas: false, calendario: false, reportes: false, ...(data.permissions || {}) });
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail));
      } finally {
        setLoaded(true);
      }
    })();
  }, [teacher?.id, teacher?.email, teacher?.name]);

  const saveAccess = async () => {
    if (enabled && !hasUser && (!accEmail.trim() || accPassword.length < 6)) {
      toast.error("Para crear el acceso: email y contraseña de al menos 6 caracteres.");
      return;
    }
    setSavingAccess(true);
    try {
      const { data } = await api.put(`/teachers/${teacher.id}/access`, {
        enabled,
        email: accEmail.trim(),
        password: accPassword || undefined,
        teacher_code: teacherCode.trim(),
        permissions,
      });
      setHasUser(!!data.has_user);
      setAccPassword("");
      toast.success(enabled ? "Acceso del profesor guardado." : "Acceso desactivado.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSavingAccess(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-3 space-y-3" data-testid="teacher-access-section">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-sky-600" />
          <span className="text-xs font-bold uppercase tracking-wider text-neutral-600">Acceso a la plataforma</span>
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-semibold">
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="teacher-access-enabled" />
          {enabled ? "Activo" : "Desactivado"}
        </label>
      </div>
      {enabled && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] font-bold uppercase text-neutral-500 mb-0.5">Email de acceso *</label>
              <input type="email" value={accEmail} onChange={(e) => setAccEmail(e.target.value)} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-testid="teacher-access-email" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-neutral-500 mb-0.5">{hasUser ? "Nueva contraseña" : "Contraseña *"}</label>
              <input type="password" value={accPassword} onChange={(e) => setAccPassword(e.target.value)} placeholder={hasUser ? "En blanco = no cambiar" : "Mínimo 6 caracteres"} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-testid="teacher-access-password" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-neutral-500 mb-0.5">Iniciales en citas</label>
              <input type="text" value={teacherCode} onChange={(e) => setTeacherCode(e.target.value.toUpperCase())} placeholder="JA" className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-500" data-testid="teacher-access-code" />
            </div>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {PERMISSION_LABELS.map((p) => (
              <label key={p.key} className="inline-flex items-center gap-2 text-sm">
                <Switch
                  checked={!!permissions[p.key]}
                  onCheckedChange={(v) => setPermissions((prev) => ({ ...prev, [p.key]: v }))}
                  data-testid={`teacher-perm-${p.key}`}
                />
                {p.label}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-neutral-500">
            El profesor solo verá sus propias citas (las asignadas a sus iniciales) y las pestañas activadas. Nunca Contabilidad, Estadísticas ni Configuración.
          </p>
        </>
      )}
      <div className="flex justify-end">
        <button type="button" onClick={saveAccess} disabled={savingAccess} className="btn-secondary !h-8 !px-3 text-xs" data-testid="teacher-access-save">
          {savingAccess ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
          Guardar acceso
        </button>
      </div>
    </div>
  );
}

export default function TeacherDialog({ open, teacher, vehicles = [], onClose, onSaved }) {
  const [name, setName] = useState("");
  const [primerApellido, setPrimerApellido] = useState("");
  const [segundoApellido, setSegundoApellido] = useState("");
  const [dni, setDni] = useState("");
  const [licenseClass, setLicenseClass] = useState("B");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zone, setZone] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [active, setActive] = useState(true);
  const [slots, setSlots] = useState(emptySlots());
  const [vacations, setVacations] = useState([]);
  const [saving, setSaving] = useState(false);

  const isEdit = !!teacher?.id;

  useEffect(() => {
    if (!open) return;
    if (teacher) {
      setName(teacher.name || "");
      setPrimerApellido(teacher.primer_apellido || "");
      setSegundoApellido(teacher.segundo_apellido || "");
      setDni(teacher.dni || "");
      setLicenseClass(teacher.license_class || "B");
      setPhone(teacher.phone || "");
      setEmail(teacher.email || "");
      setZone(teacher.zone || "");
      setVehicleId(teacher.vehicle_id || "");
      setActive(teacher.active !== false);
      setSlots(teacher.weekly_slots || emptySlots());
      setVacations(Array.isArray(teacher.vacations) ? teacher.vacations : []);
    } else {
      setName(""); setPrimerApellido(""); setSegundoApellido(""); setDni("");
      setLicenseClass("B"); setPhone(""); setEmail(""); setZone(""); setVehicleId("");
      setActive(true); setSlots(emptySlots()); setVacations([]);
    }
  }, [open, teacher]);

  const addVacation = () => setVacations([...vacations, { date_start: todayISO(), date_end: todayISO(), reason: "" }]);
  const updateVacation = (idx, field, val) => setVacations(vacations.map((v, i) => (i === idx ? { ...v, [field]: val } : v)));
  const removeVacation = (idx) => setVacations(vacations.filter((_, i) => i !== idx));

  const save = async () => {
    if (!name.trim()) { toast.error("El nombre es obligatorio."); return; }
    for (const v of vacations) {
      if (!v.date_start || !v.date_end) { toast.error("Todas las vacaciones necesitan fecha inicio y fin."); return; }
      if (v.date_end < v.date_start) { toast.error("Vacaciones: fecha fin anterior a la inicio."); return; }
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        primer_apellido: primerApellido.trim(),
        segundo_apellido: segundoApellido.trim(),
        dni: dni.trim().toUpperCase(),
        license_class: (licenseClass || "B").trim().toUpperCase(),
        phone: phone.trim(), email: email.trim(),
        zone: zone.trim(), vehicle_id: vehicleId, active,
        weekly_slots: slots,
        vacations: vacations.map((v) => ({ date_start: v.date_start, date_end: v.date_end, reason: (v.reason || "").trim() })),
      };
      if (isEdit) await api.patch(`/teachers/${teacher.id}`, body);
      else await api.post(`/teachers`, body);
      toast.success(isEdit ? "Profesor actualizado." : "Profesor creado.");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="teacher-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar profesor" : "Nuevo profesor"}</DialogTitle>
          <DialogDescription>
            Datos básicos, horario semanal, vacaciones, zona habitual y vehículo asignado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Identity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Nombre *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="teacher-name" autoFocus />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">DNI / NIE</label>
              <input type="text" value={dni} onChange={(e) => setDni(e.target.value.toUpperCase())} placeholder="12345678A" className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="teacher-dni" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Primer apellido</label>
              <input type="text" value={primerApellido} onChange={(e) => setPrimerApellido(e.target.value)} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="teacher-apellido1" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Segundo apellido</label>
              <input type="text" value={segundoApellido} onChange={(e) => setSegundoApellido(e.target.value)} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="teacher-apellido2" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Clase permiso</label>
              <select value={licenseClass} onChange={(e) => setLicenseClass(e.target.value)} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="teacher-license">
                {["B", "B+E", "A", "A1", "A2", "AM", "C", "C+E", "D", "D+E"].map((l) => (<option key={l} value={l}>{l}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Teléfono</label>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="teacher-phone" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="teacher-email" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Zona habitual</label>
              <input type="text" value={zone} onChange={(e) => setZone(e.target.value)} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="Valdemoro, Centro, Estación..." data-testid="teacher-zone" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Vehículo asignado</label>
              <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="teacher-vehicle">
                <option value="">— Sin asignar —</option>
                {vehicles.filter((v) => v.active !== false).map((v) => (
                  <option key={v.id} value={v.id}>{v.plate} — {v.brand} {v.model}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="rounded" data-testid="teacher-active" />
                Activo
              </label>
            </div>
          </div>

          {/* V1.46 · Acceso a la plataforma con conmutadores por permiso */}
          {isEdit && <TeacherAccessSection teacher={teacher} />}

          {/* Weekly slots */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">Disponibilidad semanal</label>
            <WeeklySlotsEditor value={slots} onChange={setSlots} testidPrefix="teacher-slot" />
          </div>

          {/* Vacations */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Vacaciones y ausencias</label>
              <button type="button" onClick={addVacation} className="ml-auto inline-flex items-center gap-1 h-7 rounded-lg border border-orange-200 bg-orange-50 hover:bg-orange-100 text-orange-700 px-2 text-[11px] font-bold" data-testid="teacher-vacation-add">
                <Plus className="h-3 w-3" /> Añadir
              </button>
            </div>
            {vacations.length === 0 ? (
              <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-3 text-xs text-neutral-500 text-center">Sin vacaciones registradas.</div>
            ) : (
              <div className="space-y-2">
                {vacations.map((v, i) => (
                  <div key={i} className="rounded-lg border border-neutral-200 bg-white p-2 flex flex-wrap items-end gap-2" data-testid={`teacher-vacation-${i}`}>
                    <div className="flex-1 min-w-[130px]">
                      <div className="text-[10px] uppercase text-neutral-500 font-bold mb-0.5">Motivo</div>
                      <input type="text" value={v.reason || ""} onChange={(e) => updateVacation(i, "reason", e.target.value)} className="w-full h-8 rounded border border-neutral-300 bg-white px-2 text-xs" placeholder="Vacaciones, formación..." />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-neutral-500 font-bold mb-0.5">Desde</div>
                      <input type="date" value={v.date_start || ""} onChange={(e) => updateVacation(i, "date_start", e.target.value)} className="h-8 rounded border border-neutral-300 bg-white px-2 text-xs" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-neutral-500 font-bold mb-0.5">Hasta</div>
                      <input type="date" value={v.date_end || ""} onChange={(e) => updateVacation(i, "date_end", e.target.value)} className="h-8 rounded border border-neutral-300 bg-white px-2 text-xs" />
                    </div>
                    <button type="button" onClick={() => removeVacation(i)} className="text-red-500 hover:text-red-700 p-1.5" title="Quitar">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100">
          <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
          <button type="button" onClick={save} disabled={saving} className="btn-primary" data-testid="teacher-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isEdit ? "Guardar cambios" : "Crear profesor"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
